// Sky geometry the alignments read: each planet's nearest star, a
// constellation's longest line, and the lonely stars. Computed once per
// context and cached on it.
import { dist2, segmentsCross } from '../../geometry/segments';
import { planetsOf } from '../../level/schema';
import { treeLines } from '../../play/tree';
import type { Constellation } from '../../play/types';
import type { AlignmentContext } from '../types';

const nearest = new WeakMap<AlignmentContext, Set<number>>();

/** The star nearest each planet of the sky, by id. */
export function nearestStars(ctx: AlignmentContext): Set<number> {
  let set = nearest.get(ctx);
  if (set) return set;
  set = new Set();
  for (const planet of planetsOf(ctx.level)) {
    let best: { id: number; d: number } | null = null;
    for (const node of ctx.level.nodes) {
      const d = dist2(node, planet);
      if (!best || d < best.d) best = { id: node.id, d };
    }
    if (best) set.add(best.id);
  }
  nearest.set(ctx, set);
  return set;
}

/** The two ends of a constellation's longest line (the closing line left out); the first such line when two are alike. */
export function longestLineEnds(ctx: AlignmentContext, c: Constellation): Set<number> {
  const nodes = ctx.derived.nodes;
  let best: { a: number; b: number; d: number } | null = null;
  for (const [a, b] of treeLines(c)) {
    const pa = nodes.get(a);
    const pb = nodes.get(b);
    if (!pa || !pb) continue;
    const d = dist2(pa, pb);
    if (!best || d > best.d) best = { a, b, d };
  }
  return best ? new Set([best.a, best.b]) : new Set();
}

const lonely = new WeakMap<AlignmentContext, number>();

/**
 * The lonely stars: unused stars, blocked or free, from which the straight
 * line to every other unused star crosses a drawn line; the last unused
 * star on a sky, with no other to see, is lonely too. A line ending on the
 * other star hides nothing of it.
 */
export function lonelyStars(ctx: AlignmentContext): number {
  const cached = lonely.get(ctx);
  if (cached !== undefined) return cached;
  const unused = ctx.level.nodes.filter((n) => !ctx.derived.locked.has(n.id));
  const lines = ctx.derived.lines.map(([a, b]) => [ctx.derived.nodes.get(a)!, ctx.derived.nodes.get(b)!] as const);
  let count = 0;
  if (unused.length > 0 && lines.length > 0) {
    for (const star of unused) {
      let sees = false;
      for (const other of unused) {
        if (other.id === star.id) continue;
        let hidden = false;
        for (const [p, q] of lines) {
          if (p.id === other.id || q.id === other.id || p.id === star.id || q.id === star.id) continue;
          if (segmentsCross(star, other, p, q)) {
            hidden = true;
            break;
          }
        }
        if (!hidden) {
          sees = true;
          break;
        }
      }
      if (!sees) count++;
    }
  }
  lonely.set(ctx, count);
  return count;
}
